import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Tag,
  Button,
  Input,
  DatePicker,
  message,
  Modal,
  Badge,
  // Divider,
  Collapse,
} from "antd";
import {
  LockOutlined,
  SearchOutlined,
  ReloadOutlined,
  CalendarOutlined,
  ExpandOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./style.scss";
import { APIs } from "../../utils/worker";
import Heading from "../../components/Heading";

const { Panel } = Collapse;

const LockingManagementDashboard = () => {
  const navigate = useNavigate();

  // Get logged-in user email
  const userEmail =
    JSON.parse(localStorage.getItem("userinfo"))?.username || "HO_ADMIN";

  const [cpData, setCpData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const [selectedCP, setSelectedCP] = useState(null);
  const [modalLockDates, setModalLockDates] = useState({});
  // const [lockSearchText, setLockSearchText] = useState(""); // Commented out - search functionality disabled
  const [extendReason, setExtendReason] = useState("");
  const [selectedCPIds, setSelectedCPIds] = useState([]);
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [bulkLockDates, setBulkLockDates] = useState({});
  const [bulkExtendDate, setBulkExtendDate] = useState(null);
  const [modalExtendDate, setModalExtendDate] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Fetch CPs data from API
  const fetchCPsData = async (page = 1, limit = 15, search = "") => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${APIs.delcaperBaseURLv2}/lock-system-service/admin/api/all-cps-pending-awbs`,
        {
          params: {
            page,
            limit,
            search,
          },
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data;
      setApiResponse(data);

      // Transform API response to table format
      const transformedData = data.cps.map((cp, index) => {
        // Count active locks (is_locked: true)
        const activeLocks = cp.rules.filter((rule) => rule.is_locked).length;

        // Calculate critical locks (locks older than 7 days)
        const criticalLocks = cp.rules.filter((rule) => {
          if (!rule.is_locked) return false;
          const expiryDate = moment(rule.nearest_expiry);
          return moment().diff(expiryDate, "days") > 7;
        }).length;

        // Create locks object and lockDates from rules
        const locks = {};
        const lockDates = {};
        cp.rules.forEach((rule) => {
          locks[rule.rule_id] = rule.is_locked;
          lockDates[rule.rule_id] = moment(rule.nearest_expiry).format(
            "YYYY-MM-DD"
          );
        });

        return {
          id: index + 1,
          cpCode: cp.cp_id,
          cpName: cp.cp_name || "-",
          cpCity: cp.city || "-",
          cpState: cp.state || "-",
          cpPincode: cp.pincode || "-",
          totalShipments: cp.total_pending_awbs,
          pendingOFD: cp.total_pending_awbs,
          activeLocks,
          criticalLocks,
          locks,
          lockDates,
          status: activeLocks > 0 ? "restricted" : "active",
          lastUpdated: new Date().toISOString(),
        };
      });

      setCpData(transformedData);
    } catch (error) {
      console.error("Error fetching CPs data:", error);
      message.error("Failed to load CPs data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchCPsData(currentPage, pageSize, searchText);
  }, [currentPage, pageSize, searchText]);

  // Calculate dashboard metrics
  // const metrics = {
  //   totalCPs: apiResponse?.total_cps || cpData.length,
  //   activeCPs: cpData.filter((cp) => cp.status === "active").length,
  //   restrictedCPs: cpData.filter((cp) => cp.status === "restricted").length,
  //   totalActiveLocks: cpData.reduce((sum, cp) => sum + cp.activeLocks, 0),
  //   criticalLocks: cpData.reduce((sum, cp) => sum + cp.criticalLocks, 0),
  //   pendingOFDTotal: cpData.reduce((sum, cp) => sum + cp.pendingOFD, 0),
  // };

  const handleOpenExtendModal = (cp) => {
    setSelectedCP(cp);
    setModalLockDates({ ...cp.lockDates });
    setExtendModalVisible(true);
  };

  const handleNavigateToPendingAwbs = (cpId) => {
    if (!cpId) {
      message.error("CP ID not found");
      return;
    }
    navigate(`/pending-awbs?cp_id=${cpId}`);
  };

  const handleCloseExtendModal = () => {
    setExtendModalVisible(false);
    setSelectedCP(null);
    setModalLockDates({});
    // setLockSearchText(""); // Commented out - search functionality disabled
    setExtendReason("");
    setModalExtendDate(null);
  };

  const handleModalDateChange = (lockKey, date) => {
    if (!selectedCP || !modalLockDates[lockKey]) return;

    const today = moment().startOf("day");
    const newDate = moment(date).startOf("day");
    const daysDiff = newDate.diff(today, "days");

    // Validate: new date cannot be in the past
    if (daysDiff < 0) {
      message.error("Lock date cannot be in the past");
      return;
    }

    // Validate: maximum 4 days extension
    if (daysDiff > 4) {
      message.error("Lock date cannot be more than 4 days from today");
      return;
    }

    setModalLockDates((prev) => ({
      ...prev,
      [lockKey]: newDate.format("YYYY-MM-DD"),
    }));
  };

  const handleModalExtendDateSelect = (date) => {
    setModalExtendDate(date);

    if (!date || !selectedCP) return;

    const updatedDates = { ...modalLockDates };
    let totalLocksExtended = 0;

    // Calculate extension from today to selected date
    const today = moment().startOf("day");
    const selectedDate = moment(date).startOf("day");
    const daysDiff = selectedDate.diff(today, "days");

    // Validate: selected date must be in the future
    if (daysDiff < 0) {
      message.error("Selected date cannot be in the past");
      return;
    }

    // Validate: maximum 4 days extension
    if (daysDiff > 4) {
      message.error("Extension date cannot be more than 4 days from today");
      return;
    }

    // Iterate through ALL locks from selected CP (both active and inactive)
    Object.keys(selectedCP.locks).forEach((lockKey) => {
      updatedDates[lockKey] = selectedDate.format("YYYY-MM-DD");
      totalLocksExtended++;
    });

    setModalLockDates(updatedDates);
    message.success(
      `${totalLocksExtended} lock date(s) updated to ${selectedDate.format(
        "DD-MMM-YY"
      )}`
    );
  };

  const handleExtendAllLocks = async () => {
    if (!selectedCP) return;

    // Validate that a date has been selected (required)
    if (!modalExtendDate) {
      message.error("Please select an extend date (required)");
      return;
    }

    // Validate reason is provided (required)
    if (!extendReason || extendReason.trim() === "") {
      message.error("Please provide a reason for extending locks (required)");
      return;
    }

    // Calculate extension from today to selected date
    const today = moment().startOf("day");
    const selectedDate = moment(modalExtendDate).startOf("day");
    const totalHours = selectedDate.diff(today, "hours");
    const extension_days = Math.floor(totalHours / 24);
    const extension_hours = totalHours % 24;

    // Validate: extension must be positive
    if (totalHours <= 0) {
      message.error("Extension date must be in the future");
      return;
    }

    // Get ALL locks (rule_ids) - both active and inactive
    const allRuleIds = Object.keys(selectedCP.locks);

    if (allRuleIds.length === 0) {
      message.error("No locks to extend");
      return;
    }

    Modal.confirm({
      title: "Extend Lock Dates",
      content: (
        <div>
          <p>
            Are you sure you want to extend locks for {selectedCP.cpName} (
            {selectedCP.cpCode})?
          </p>
          <p>
            <strong>Total Locks:</strong> {allRuleIds.length}
          </p>
          <p>
            <strong>Extension:</strong> {extension_days} days{" "}
            {extension_hours > 0 && `${extension_hours} hours`}
          </p>
          <p>
            <strong>Reason:</strong> {extendReason}
          </p>
        </div>
      ),
      okText: "Yes, Extend",
      cancelText: "Cancel",
      icon: <CheckCircleOutlined style={{ color: "#1890ff" }} />,
      onOk: async () => {
        const loadingMessage = message.loading("Extending locks...", 0);

        try {
          const payload = {
            cp_details: [
              {
                cp_id: selectedCP.cpCode,
                rule_ids: allRuleIds,
              },
            ],
            extension_days,
            extension_hours,
            reason: extendReason,
            requested_by: userEmail,
          };

          const response = await axios.post(
            `${APIs.delcaperBaseURLv2}/lock-system-service/api/extensions/selective/apply`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          loadingMessage();

          if (response.data.success) {
            // Use the message from API response
            message.success(
              response.data.message || "Locks extended successfully"
            );

            // Refresh the CP data
            await fetchCPsData(currentPage, pageSize, searchText);

            handleCloseExtendModal();
          } else {
            message.error(response.data.message || "Failed to extend locks");
          }
        } catch (error) {
          loadingMessage();
          console.error("Error extending locks:", error);
          message.error(
            error.response?.data?.message ||
              "Failed to extend locks. Please try again."
          );
        }
      },
    });
  };

  // Bulk Update Handlers
  const handleSelectCP = (cpId, checked) => {
    if (checked) {
      setSelectedCPIds((prev) => [...prev, cpId]);
    } else {
      setSelectedCPIds((prev) => prev.filter((id) => id !== cpId));
    }
  };

  const handleSelectAllCPs = (checked) => {
    if (checked) {
      setSelectedCPIds(cpData.map((cp) => cp.id));
    } else {
      setSelectedCPIds([]);
    }
  };

  const handleOpenBulkModal = () => {
    const initialDates = {};
    selectedCPIds.forEach((cpId) => {
      const cp = cpData.find((c) => c.id === cpId);
      if (cp) {
        initialDates[cpId] = { ...cp.lockDates };
      }
    });
    setBulkLockDates(initialDates);
    setBulkModalVisible(true);
  };

  const handleCloseBulkModal = () => {
    setBulkModalVisible(false);
    setBulkLockDates({});
    setBulkExtendDate(null);
  };

  const handleBulkDateChange = (cpId, lockKey, date) => {
    const cp = cpData.find((c) => c.id === cpId);
    if (!cp || !bulkLockDates[cpId] || !bulkLockDates[cpId][lockKey]) return;

    const today = moment().startOf("day");
    const newDate = moment(date).startOf("day");
    const daysDiff = newDate.diff(today, "days");

    // Validate: new date cannot be in the past
    if (daysDiff < 0) {
      message.error("Lock date cannot be in the past");
      return;
    }

    // Validate: maximum 4 days extension
    if (daysDiff > 4) {
      message.error("Lock date cannot be more than 4 days from today");
      return;
    }

    setBulkLockDates((prev) => ({
      ...prev,
      [cpId]: {
        ...prev[cpId],
        [lockKey]: newDate.format("YYYY-MM-DD"),
      },
    }));
  };

  const handleBulkDateSelect = (date) => {
    setBulkExtendDate(date);

    if (!date) return;

    // Calculate extension from today to selected date
    const today = moment().startOf("day");
    const selectedDate = moment(date).startOf("day");
    const daysDiff = selectedDate.diff(today, "days");

    // Validate: selected date must be in the future
    if (daysDiff < 0) {
      message.error("Selected date cannot be in the past");
      return;
    }

    // Validate: maximum 4 days extension
    if (daysDiff > 4) {
      message.error("Extension date cannot be more than 4 days from today");
      return;
    }

    const updatedDates = { ...bulkLockDates };
    let totalLocksExtended = 0;

    selectedCPIds.forEach((cpId) => {
      const cp = cpData.find((c) => c.id === cpId);
      if (!cp) return;

      updatedDates[cpId] = { ...bulkLockDates[cpId] };

      // Iterate through ALL locks from the CP (both active and inactive)
      Object.keys(cp.locks).forEach((lockKey) => {
        updatedDates[cpId][lockKey] = selectedDate.format("YYYY-MM-DD");
        totalLocksExtended++;
      });
    });

    setBulkLockDates(updatedDates);
    message.success(
      `${totalLocksExtended} lock date(s) updated to ${selectedDate.format(
        "DD-MMM-YY"
      )}`
    );
  };

  const handleBulkUpdateAllDates = async () => {
    // Validate that a date has been selected (required)
    if (!bulkExtendDate) {
      message.error("Please select an extend date (required)");
      return;
    }

    // Calculate extension from today to selected date
    const today = moment().startOf("day");
    const selectedDate = moment(bulkExtendDate).startOf("day");
    const totalHours = selectedDate.diff(today, "hours");
    const extension_days = Math.floor(totalHours / 24);
    const extension_hours = totalHours % 24;

    // Validate: extension must be positive
    if (totalHours <= 0) {
      message.error("Extension date must be in the future");
      return;
    }

    // Build cp_details array
    const cpDetails = [];
    let totalLocks = 0;

    selectedCPIds.forEach((cpId) => {
      const cp = cpData.find((c) => c.id === cpId);
      if (!cp) return;

      // Get ALL locks for this CP (both active and inactive)
      const allRuleIds = Object.keys(cp.locks);

      if (allRuleIds.length > 0) {
        cpDetails.push({
          cp_id: cp.cpCode,
          rule_ids: allRuleIds,
        });
        totalLocks += allRuleIds.length;
      }
    });

    if (cpDetails.length === 0) {
      message.error("No locks to extend in selected CPs");
      return;
    }

    Modal.confirm({
      title: "Bulk Extend Lock Dates",
      content: (
        <div>
          <p>
            Are you sure you want to extend locks for {cpDetails.length} CP(s)?
          </p>
          <p>
            <strong>Total Locks:</strong> {totalLocks}
          </p>
          <p>
            <strong>Extension:</strong> {extension_days} days{" "}
            {extension_hours > 0 && `${extension_hours} hours`}
          </p>
          <p>
            <strong>Reason:</strong> {extendReason}
          </p>
        </div>
      ),
      okText: "Yes, Extend All",
      cancelText: "Cancel",
      icon: <CheckCircleOutlined style={{ color: "#1890ff" }} />,
      onOk: async () => {
        const loadingMessage = message.loading(
          "Extending locks for multiple CPs...",
          0
        );

        try {
          const payload = {
            cp_details: cpDetails,
            extension_days,
            extension_hours,
            reason: extendReason,
            requested_by: userEmail,
          };

          const response = await axios.post(
            `${APIs.delcaperBaseURLv2}/lock-system-service/api/extensions/selective/apply`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          loadingMessage();

          if (response.data.success) {
            // Use the message from API response
            message.success(
              response.data.message || "Locks extended successfully"
            );

            // Refresh the CP data
            await fetchCPsData(currentPage, pageSize, searchText);

            handleCloseBulkModal();
            setSelectedCPIds([]);
          } else {
            message.error(response.data.message || "Failed to extend locks");
          }
        } catch (error) {
          loadingMessage();
          console.error("Error extending locks:", error);
          message.error(
            error.response?.data?.message ||
              "Failed to extend locks. Please try again."
          );
        }
      },
    });
  };

  const handleRefresh = () => {
    fetchCPsData(currentPage, pageSize, searchText);
  };

  // Table columns
  const columns = [
    {
      title: (
        <input
          type="checkbox"
          checked={
            selectedCPIds.length === cpData.length &&
            cpData.length > 0
          }
          onChange={(e) => handleSelectAllCPs(e.target.checked)}
          style={{ cursor: "pointer" }}
        />
      ),
      key: "checkbox",
      width: 50,
      render: (_, record) => (
        <input
          type="checkbox"
          checked={selectedCPIds.includes(record.id)}
          onChange={(e) => handleSelectCP(record.id, e.target.checked)}
          style={{ cursor: "pointer" }}
        />
      ),
    },
    {
      title: "CP Code",
      dataIndex: "cpCode",
      key: "cpCode",
      width: 80,
      align: "left",
      render: (text) => (
        <div className="cp-code-cell">
          <strong>{text}</strong>
        </div>
      ),
    },
    {
      title: "CP Name",
      dataIndex: "cpName",
      key: "cpName",
      width: 200,
      align: "left",
    },
    {
      title: "City",
      dataIndex: "cpCity",
      key: "cpCity",
      width: 80,
      align: "left",
    },
    {
      title: "State",
      dataIndex: "cpState",
      key: "cpState",
      width: 80,
      align: "left",
    },
    {
      title: "Pincode",
      dataIndex: "cpPincode",
      key: "cpPincode",
      width: 80,
      align: "left",
    },
    {
      title: "Pending AWBs",
      dataIndex: "pendingOFD",
      key: "pendingOFD",
      width: 200,
      align: "left",
      render: (text, record) => {
        const count = text || 0;
        return (
          <span
            onClick={() =>
              count > 0 && handleNavigateToPendingAwbs(record.cpCode)
            }
            style={{
              cursor: count > 0 ? "pointer" : "default",
              color: count > 0 ? "#1890ff" : "inherit",
              textDecoration: count > 0 ? "underline" : "none",
              fontWeight: count > 0 ? "600" : "normal",
            }}
          >
            {count}
          </span>
        );
      },
    },
    // {
    //   title: "CP Status",
    //   dataIndex: "status",
    //   key: "status",
    //   width: 120,
    //   align: "center",
    //   render: (status) => {
    //     const color = status === "active" ? "green" : "red";
    //     const text = status === "active" ? "Active" : "Restricted";
    //     return <Tag color={color}>{text}</Tag>;
    //   },
    // },
    {
      title: "Active Locks",
      dataIndex: "activeLocks",
      key: "activeLocks",
      width: 120,
      align: "center",
      render: (value) => (
        <Badge
          count={value}
          showZero
          style={{
            backgroundColor: value > 0 ? "#ff4d4f" : "#52c41a",
          }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<ExpandOutlined />}
          onClick={() => handleOpenExtendModal(record)}
        >
          Extend Locks
        </Button>
      ),
    },
  ];

  return (
    <div className="ho-operations-dashboard">
      <Heading title={"Locking Management"} />
      {/*
      <Row gutter={[12, 12]} className="metrics-section">
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card className="metric-card total">
            <div className="metric-value">{metrics.totalCPs}</div>
            <div className="metric-label">Total CPs</div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card className="metric-card total">
            <div className="metric-value">{metrics.activeCPs}</div>
            <div className="metric-label">Active CPs</div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card className="metric-card restricted">
            <div className="metric-value">{metrics.restrictedCPs}</div>
            <div className="metric-label">Restricted CPs</div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card className="metric-card locks">
            <div className="metric-value">{metrics.totalActiveLocks}</div>
            <div className="metric-label">Active Locks</div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card className="metric-card critical">
            <div className="metric-value">{metrics.criticalLocks}</div>
            <div className="metric-label">Critical Locks</div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card className="metric-card pending">
            <div className="metric-value">{metrics.pendingOFDTotal}</div>
            <div className="metric-label">Pending OFD</div>
          </Card>
        </Col>
      </Row> */}

      {/* Filters and Search */}
      <Card className="filters-card">
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder="Search CP Name, City, State, Pincode"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              block
            >
              Refresh Data
            </Button>
          </Col>
          {selectedCPIds.length > 0 && (
            <Col xs={24} sm={12} md={8} lg={5}>
              <Button
                type="primary"
                icon={<ExpandOutlined />}
                onClick={handleOpenBulkModal}
                block
                style={{ background: "#52c41a", borderColor: "#52c41a" }}
              >
                Bulk Extend ({selectedCPIds.length} CPs)
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* CP List Table */}
      <Card className="table-card">
        <Table
          columns={columns}
          dataSource={cpData}
          rowKey="id"
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: apiResponse?.total_cps || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} CPs`,
            pageSizeOptions: ["15", "30", "50", "100"],
            position: ["bottomRight"],
            responsive: true,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          className="cp-table"
        />
      </Card>

      {/* Extend Locks Modal */}
      <Modal
        title={null}
        open={extendModalVisible}
        onCancel={handleCloseExtendModal}
        width={920}
        footer={null}
        className="extend-locks-modal"
        centered
      >
        {selectedCP && (
          <div className="modal-content">
            {/* Modal Header */}
            <div className="modal-custom-header">
              {/* Title Row */}
              <div className="header-title-row">
                <div className="title-left">
                  <LockOutlined style={{ fontSize: 20, color: "#1890ff" }} />
                  <h3>Extend Locks</h3>
                </div>
              </div>

              {/* Control Row - Channel Partner Info */}
              <div className="header-control-row">
                <div className="control-left">
                  <span className="cp-info">
                    {selectedCP.cpName} ({selectedCP.cpCode})
                  </span>
                </div>
                {/* <div className="control-right">
                  <div className="info-badges">
                    <div className="info-item">
                      <span className="label">Location:</span>
                      <span className="value">{selectedCP.location}</span>
                    </div>
                    <Divider type="vertical" />
                  </div>
                </div> */}
              </div>

              {/* Date Selection and Search Row */}
              <div className="header-date-row">
                <div className="date-section">
                  <div className="date-label-wrapper">
                    <span className="date-label">
                      Select Extend Date{" "}
                      <span style={{ color: "#ff4d4f" }}>*</span>
                    </span>
                    <span className="date-info">(max 4 days from today)</span>
                  </div>
                  <DatePicker
                    value={modalExtendDate}
                    onChange={handleModalExtendDateSelect}
                    format="DD-MMM-YYYY"
                    size="middle"
                    placeholder="Select date to extend (required)"
                    className="modal-date-picker"
                    suffixIcon={<CalendarOutlined />}
                    disabledDate={(current) => {
                      const today = moment().startOf("day");
                      const maxDate = moment().add(4, "days").endOf("day");
                      // Disable dates in the past and dates more than 4 days from today
                      return current && (current < today || current > maxDate);
                    }}
                  />
                </div>

                {/* <div className="search-section">
                  <Input
                    placeholder="Search locks by name..."
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    value={lockSearchText}
                    onChange={(e) => setLockSearchText(e.target.value)}
                    allowClear
                    size="middle"
                    className="search-input"
                  />
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    size="middle"
                    className="search-button"
                  >
                    Search
                  </Button>
                </div> */}

                <div className="reason-section">
                  <div className="reason-label-wrapper">
                    <span className="reason-label">Reason for Extension</span>
                    <span className="reason-info">(required)</span>
                  </div>
                  <Input.TextArea
                    placeholder="Enter reason for extending locks..."
                    value={extendReason}
                    onChange={(e) => setExtendReason(e.target.value)}
                    rows={1}
                    maxLength={500}
                    className="reason-input"
                  />
                </div>
              </div>
            </div>

            {/* Locks Grid */}
            <div className="locks-grid">
              <div className="category-section">
                <div className="category-header">
                  <h4>All Locks</h4>
                  <Badge
                    count={Object.keys(selectedCP.locks).length}
                    showZero
                    className="category-badge"
                  />
                </div>
                <div className="locks-list">
                  {Object.keys(selectedCP.locks).map((lockKey) => {
                    const isActive = selectedCP.locks[lockKey];
                    const lockDate = modalLockDates[lockKey];
                    const originalLockDate = selectedCP.lockDates[lockKey];
                    // Check if date has changed
                    const normalizedOriginal = originalLockDate
                      ? moment(originalLockDate).format("YYYY-MM-DD")
                      : null;
                    const normalizedCurrent = lockDate
                      ? moment(lockDate).format("YYYY-MM-DD")
                      : null;
                    const hasChanged =
                      normalizedOriginal &&
                      normalizedCurrent &&
                      normalizedOriginal !== normalizedCurrent;

                    // Format lock name from rule_id
                    const lockName = lockKey
                      .split("_")
                      .map(
                        (word) =>
                          word.charAt(0).toUpperCase() +
                          word.slice(1).toLowerCase()
                      )
                      .join(" ");

                    return (
                      <div
                        key={lockKey}
                        className={`lock-item ${
                          isActive ? "lock-active" : "lock-inactive"
                        }`}
                      >
                        <div className="lock-header">
                          <div className="lock-title">
                            <LockOutlined
                              className={`lock-icon ${
                                isActive ? "active" : ""
                              }`}
                            />
                            <span className="lock-text">{lockName}</span>
                          </div>
                          {isActive && <Tag color="error">Active</Tag>}
                        </div>
                        <div className="date-display-section">
                          {hasChanged && (
                            <div className="original-date">
                              <span className="strikethrough">
                                {moment(originalLockDate).format("DD-MMM-YY")}
                              </span>
                              <span className="arrow">→</span>
                              <span className="new-date">
                                {moment(lockDate).format("DD-MMM-YY")}
                              </span>
                            </div>
                          )}
                          <DatePicker
                            value={
                              lockDate ? moment(lockDate, "YYYY-MM-DD") : null
                            }
                            onChange={(date) =>
                              handleModalDateChange(lockKey, date)
                            }
                            format="DD-MMM-YY"
                            disabled={true}
                            size="middle"
                            disabledDate={(current) => {
                              const today = moment().startOf("day");
                              const maxDate = moment()
                                .add(4, "days")
                                .endOf("day");
                              // Disable dates in the past and dates more than 4 days from today
                              return (
                                current &&
                                (current < today || current > maxDate)
                              );
                            }}
                            suffixIcon={
                              <CalendarOutlined style={{ fontSize: 14 }} />
                            }
                            placeholder="No Date"
                            className="lock-datepicker"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-custom-footer">
              <div className="footer-actions">
                <Button size="middle" onClick={handleCloseExtendModal}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="middle"
                  onClick={handleExtendAllLocks}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Extend Modal */}
      <Modal
        title={null}
        open={bulkModalVisible}
        onCancel={handleCloseBulkModal}
        width={960}
        footer={null}
        className="bulk-extend-modal"
        centered
      >
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-custom-header">
            {/* Title Row */}
            <div className="header-title-row">
              <div className="title-left">
                <LockOutlined style={{ fontSize: 20, color: "#1890ff" }} />
                <h3>Bulk Extend Lock Dates</h3>
              </div>
            </div>

            {/* Control Row - Channel Partner Info */}
            <div className="header-control-row">
              <div className="control-left">
                <span className="cp-info">
                  {selectedCPIds.length} Channel Partner(s) selected
                </span>
              </div>
            </div>

            {/* Date Selection and Reason Row */}
            <div className="header-date-row">
              <div className="date-section">
                <div className="date-label-wrapper">
                  <span className="date-label">
                    Select Extend Date{" "}
                    <span style={{ color: "#ff4d4f" }}>*</span>
                  </span>
                  <span className="date-info">(max 4 days from today)</span>
                </div>
                <DatePicker
                  value={bulkExtendDate}
                  onChange={handleBulkDateSelect}
                  format="DD-MMM-YYYY"
                  size="middle"
                  placeholder="Select date to extend (required)"
                  className="modal-date-picker"
                  suffixIcon={<CalendarOutlined />}
                  disabledDate={(current) => {
                    const today = moment().startOf("day");
                    const maxDate = moment().add(4, "days").endOf("day");
                    // Disable dates in the past and dates more than 4 days from today
                    return current && (current < today || current > maxDate);
                  }}
                />
              </div>

              <div className="reason-section">
                <div className="reason-label-wrapper">
                  <span className="reason-label">Reason for Extension</span>
                  <span className="reason-info">(required)</span>
                </div>
                <Input.TextArea
                  placeholder="Enter reason for extending locks..."
                  value={extendReason}
                  onChange={(e) => setExtendReason(e.target.value)}
                  rows={1}
                  maxLength={500}
                  className="reason-input"
                />
              </div>
            </div>
          </div>

          {/* CP Collapsible List */}
          <div className="bulk-cp-list">
            <Collapse>
              {selectedCPIds.map((cpId) => {
                const cp = cpData.find((c) => c.id === cpId);
                if (!cp) return null;

                return (
                  <Panel
                    header={
                      <div className="cp-panel-header">
                        <div className="cp-info">
                          <strong>{cp.cpName}</strong>
                          <span className="cp-code">({cp.cpCode})</span>
                        </div>
                        <Tag
                          color={cp.status === "active" ? "success" : "error"}
                        >
                          {cp.status === "active" ? "Active" : "Restricted"}
                        </Tag>
                      </div>
                    }
                    key={cp.id}
                  >
                    <div className="locks-grid-bulk">
                      <div className="category-section">
                        <div className="category-header">
                          <h4>All Locks</h4>
                          <Badge
                            count={Object.keys(cp.locks).length}
                            showZero
                            className="category-badge"
                          />
                        </div>
                        <div className="locks-list">
                          {Object.keys(cp.locks).map((lockKey) => {
                            const isActive = cp.locks[lockKey];

                            const lockDate = bulkLockDates[cpId]?.[lockKey];
                            const originalDate = cp.lockDates[lockKey];
                            const currentDate = lockDate
                              ? moment(lockDate)
                              : null;
                            // Compare dates properly by normalizing both to YYYY-MM-DD format
                            const normalizedOriginal = originalDate
                              ? moment(originalDate).format("YYYY-MM-DD")
                              : null;
                            const normalizedCurrent = lockDate
                              ? moment(lockDate).format("YYYY-MM-DD")
                              : null;
                            const hasChanged =
                              normalizedOriginal &&
                              normalizedCurrent &&
                              normalizedOriginal !== normalizedCurrent;

                            // Format lock name from rule_id
                            const lockName = lockKey
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase()
                              )
                              .join(" ");

                            return (
                              <div
                                key={lockKey}
                                className={`lock-item ${
                                  isActive ? "lock-active" : "lock-inactive"
                                }`}
                              >
                                <div className="lock-header">
                                  <div className="lock-title">
                                    <LockOutlined
                                      className={`lock-icon ${
                                        isActive ? "active" : ""
                                      }`}
                                    />
                                    <span className="lock-text">
                                      {lockName}
                                    </span>
                                  </div>
                                  {isActive && <Tag color="error">Active</Tag>}
                                </div>
                                <div className="date-display-section">
                                  {hasChanged && (
                                    <div className="original-date">
                                      <span className="strikethrough">
                                        {moment(originalDate).format(
                                          "DD-MMM-YY"
                                        )}
                                      </span>
                                      <span className="arrow">→</span>
                                      <span className="new-date">
                                        {moment(lockDate).format("DD-MMM-YY")}
                                      </span>
                                    </div>
                                  )}
                                  <DatePicker
                                    value={currentDate}
                                    onChange={(date) =>
                                      handleBulkDateChange(cpId, lockKey, date)
                                    }
                                    format="DD-MMM-YY"
                                    disabled={true}
                                    size="middle"
                                    disabledDate={(current) => {
                                      const today = moment().startOf("day");
                                      const maxDate = moment()
                                        .add(4, "days")
                                        .endOf("day");
                                      // Disable dates in the past and dates more than 4 days from today
                                      return (
                                        current &&
                                        (current < today || current > maxDate)
                                      );
                                    }}
                                    suffixIcon={
                                      <CalendarOutlined
                                        style={{ fontSize: 14 }}
                                      />
                                    }
                                    placeholder="No Date"
                                    className="lock-datepicker"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </Panel>
                );
              })}
            </Collapse>
          </div>

          {/* Modal Footer */}
          <div className="modal-custom-footer">
            <div className="footer-actions">
              <Button size="large" onClick={handleCloseBulkModal}>
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                onClick={handleBulkUpdateAllDates}
              >
                Update All Dates
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LockingManagementDashboard;
